import { countMetric, distributionMetric } from '@/lib/metrics.ts';

import {
    type CheckinEntry,
    type CheckinType,
    type MetricFailureReason,
    MetricName,
    type MetricOutcome,
} from '@/models/metrics.ts';

/**
 * Check-in observability telemetry: thin, fire-and-forget domain helpers over the typed metrics
 * wrapper. `reason` is a closed `MetricFailureReason`, only attached on `failure`.
 */

interface CheckinOutcomeParams {
    outcome: MetricOutcome;
    checkinType: CheckinType;
    entry: CheckinEntry;
    /** Failure tag, attached only when `outcome === 'failure'`. */
    reason?: MetricFailureReason;
    /** Elapsed arrive→confirm time in ms; emits `careflow.checkin.duration` when present on a success. */
    durationMs?: number;
}

/**
 * Emit `careflow.checkin.outcome` and, on success with a duration, `careflow.checkin.duration`.
 * Callers must emit exactly once per check-in (emit-once-and-clear via the persisted start stamp).
 */
const trackCheckinOutcome = ({ outcome, checkinType, entry, reason, durationMs }: CheckinOutcomeParams): void => {
    countMetric(MetricName.CHECKIN_OUTCOME, 1, {
        outcome: outcome,
        checkin_type: checkinType,
        entry: entry,
        reason: outcome === 'failure' ? reason : undefined,
    });

    if (outcome === 'success' && durationMs !== undefined) {
        distributionMetric(MetricName.CHECKIN_DURATION, durationMs, { checkin_type: checkinType, entry: entry });
    }
};

interface CardReadOutcomeParams {
    outcome: MetricOutcome;
    /** Failure tag, attached only when `outcome === 'failure'`. */
    reason?: MetricFailureReason;
}

/** Emit `careflow.card_read.outcome`, counted per attempt (retry-then-success = one failure + one success). */
const trackCardReadOutcome = ({ outcome, reason }: CardReadOutcomeParams): void => {
    countMetric(MetricName.CARD_READ_OUTCOME, 1, {
        outcome: outcome,
        reason: outcome === 'failure' ? reason : undefined,
    });
};

export { trackCardReadOutcome, trackCheckinOutcome };
